import { useState, useRef, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { 
  BookOpen, 
  CreditCard, 
  Smartphone, 
  GraduationCap, 
  Globe, 
  Award, 
  Clock, 
  Star, 
  Calendar, 
  Video, 
  CheckCircle, 
  MapPin,
  ChevronLeft,
  ChevronRight,
  FileText,
  Settings,
  DollarSign,
  Upload,
  Link as LinkIcon,
  Plus
} from 'lucide-react';
import TutorReviews from '@/components/TutorReviews';
import LessonCard from '@/components/LessonCard';
import { TutorPostsTab } from '@/components/TutorPostsTab';
import { PostInputSection } from '@/components/PostInputSection';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import CreateLessonModal from '@/components/CreateLessonModal';
import { convertToEmbeddableUrl } from '@/lib/utils';
import { pb } from '@/lib/pocketbase';
import { isSameDay, parseISO } from 'date-fns';

interface TutorProfileTabsProps {
  tutorData: any;
  profilePictureUrl: string;
  isUploadingPicture: boolean;
  showProfilePictureSection: boolean;
  setShowProfilePictureSection: (show: boolean) => void;
  handleFileUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleUrlSubmit: () => void;
  setProfilePictureUrl: (url: string) => void;
  profilePictureUrlInput: string;
  setIsEditModalOpen: (open: boolean) => void;
  setIsChangePasswordModalOpen: (open: boolean) => void;
  setIsAddStoryModalOpen: (open: boolean) => void;
  isOwnProfile: boolean;
  renderExperience: (experience: any) => string | null;
  getYouTubeVideoId: (url: string) => string | null;
  categorizeSubjects: (subjects: string[]) => { defaultOnes: string[]; approvedOnes: string[] };
  lessons: any[];
  onEditSection?: (section: string) => void;
}

const TutorProfileTabs = ({
  tutorData,
  profilePictureUrl,
  isUploadingPicture,
  showProfilePictureSection,
  setShowProfilePictureSection,
  handleFileUpload,
  handleUrlSubmit,
  setProfilePictureUrl,
  profilePictureUrlInput,
  setIsEditModalOpen,
  setIsChangePasswordModalOpen,
  setIsAddStoryModalOpen,
  isOwnProfile,
  renderExperience,
  getYouTubeVideoId,
  categorizeSubjects,
  lessons,
  onEditSection
}: TutorProfileTabsProps) => {
  const tabsListRef = useRef<HTMLDivElement>(null);
  const [showFullBio, setShowFullBio] = useState(false);
  const [selectedAvailabilityDate, setSelectedAvailabilityDate] = useState<Date>();
  const [isCreateLessonModalOpen, setIsCreateLessonModalOpen] = useState(false);
  const [bookings, setBookings] = useState<any[]>([]);
  const [isLoadingBookings, setIsLoadingBookings] = useState(false);

  // Fetch bookings for this tutor to show on calendar
  useEffect(() => {
    const fetchBookings = async () => {
      if (!tutorData?.id) return;
      
      setIsLoadingBookings(true);
      try {
        // Fetch all bookings for this tutor (like reference implementation)
        const bookingsData = await pb.collection('bookings').getFullList({
          filter: `tutorId="${tutorData.id}"`,
          sort: 'date,startTime'
        });
        
        // Filter to only non-cancelled bookings
        const activeBookings = bookingsData.filter(
          booking => booking.status !== 'cancelled' && booking.status !== 'rejected'
        );
        
        console.log('Fetched bookings:', activeBookings.length, 'total active bookings');
        setBookings(activeBookings);
      } catch (error) {
        console.error('Error fetching bookings:', error);
      } finally {
        setIsLoadingBookings(false);
      }
    };

    fetchBookings();
  }, [tutorData?.id]);

  const scrollTabs = (direction: 'left' | 'right') => {
    if (tabsListRef.current) {
      const scrollAmount = 200;
      const newScrollLeft = direction === 'left' 
        ? tabsListRef.current.scrollLeft - scrollAmount
        : tabsListRef.current.scrollLeft + scrollAmount;
      
      tabsListRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth'
      });
    }
  };

  const { defaultOnes, approvedOnes } = categorizeSubjects(tutorData?.subjects || []);
  
  // Helper function to parse booking dates
  const parseBookingDate = (dateString: string) => {
    try {
      if (dateString.includes('T') || dateString.includes(' ')) {
        const datePart = dateString.split('T')[0].split(' ')[0];
        const [year, month, day] = datePart.split('-').map(Number);
        return new Date(year, month - 1, day);
      } else {
        const [year, month, day] = dateString.split('-').map(Number);
        return new Date(year, month - 1, day);
      }
    } catch (error) {
      console.error('Error parsing date:', dateString, error);
      return new Date();
    }
  };

  // Get dates that have bookings
  const getDaysWithBookings = () => {
    return bookings.map(booking => parseBookingDate(booking.date));
  };

  // Get bookings for a specific date
  const getBookingsForDate = (date: Date) => {
    return bookings.filter(booking => {
      const bookingDate = parseBookingDate(booking.date);
      return isSameDay(bookingDate, date);
    });
  };
  
  // Calculate available time slots for selected date
  const getAvailableTimeSlotsForDate = (date: Date) => {
    if (!tutorData?.availability?.timeSlots) return [];
    
    const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
    const daySlot = tutorData.availability.timeSlots.find(
      (slot: any) => slot.day.toLowerCase() === dayName.toLowerCase()
    );
    
    return daySlot?.slots || [];
  };

  const availableTimeSlots = selectedAvailabilityDate 
    ? getAvailableTimeSlotsForDate(selectedAvailabilityDate)
    : [];

  const bookedTimeSlots = selectedAvailabilityDate
    ? getBookingsForDate(selectedAvailabilityDate)
    : [];

  return (
    <>
    <Tabs defaultValue="posts" className="w-full">
      <div className="relative">
        {/* Scroll arrows for desktop */}
        <Button
          variant="ghost"
          size="sm"
          className="hidden xl:flex absolute left-0 top-1/2 -translate-y-1/2 z-10 h-8 w-8 rounded-full bg-white shadow-md hover:bg-gray-100"
          onClick={() => scrollTabs('left')}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>

        <Button
          variant="ghost"
          size="sm"
          className="hidden xl:flex absolute right-0 top-1/2 -translate-y-1/2 z-10 h-8 w-8 rounded-full bg-white shadow-md hover:bg-gray-100"
          onClick={() => scrollTabs('right')}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>

        <div ref={tabsListRef} className="overflow-x-auto tabs-scroll-container">
          <TabsList className="w-full justify-start h-auto flex-nowrap rounded-none border-b bg-transparent p-0">
            <TabsTrigger value="posts" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent whitespace-nowrap px-4 py-3">
              <FileText className="h-4 w-4 mr-1" />
              Posts
            </TabsTrigger>
            <TabsTrigger value="about" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent whitespace-nowrap px-4 py-3">
              About
            </TabsTrigger>
            <TabsTrigger value="subjects" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent whitespace-nowrap px-4 py-3">
              Subjects
            </TabsTrigger>
            <TabsTrigger value="experience" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent whitespace-nowrap px-4 py-3">
              Experience
            </TabsTrigger>
            <TabsTrigger value="availability" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent whitespace-nowrap px-4 py-3">
              Availability
            </TabsTrigger>
            <TabsTrigger value="pricing" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent whitespace-nowrap px-4 py-3">
              Pricing
            </TabsTrigger>
            <TabsTrigger value="lessons" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent whitespace-nowrap px-4 py-3">
              Lessons
            </TabsTrigger>
            <TabsTrigger value="video" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent whitespace-nowrap px-4 py-3">
              Video
            </TabsTrigger>
            <TabsTrigger value="reviews" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent whitespace-nowrap px-4 py-3">
              Reviews
            </TabsTrigger>
            {isOwnProfile && (
              <TabsTrigger value="settings" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent whitespace-nowrap px-4 py-3">
                <Settings className="h-4 w-4 mr-1" />
                Settings
              </TabsTrigger>
            )}
          </TabsList>
        </div>
      </div>

      {/* Posts Tab with Create Post Interface */}
      <TabsContent value="posts" className="mt-4 space-y-4">
        {isOwnProfile && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Create a Post</CardTitle>
            </CardHeader>
            <CardContent>
              <PostInputSection />
            </CardContent>
          </Card>
        )}
        
        <Card>
          <CardContent className="p-2 md:p-4">
            <TutorPostsTab tutorId={tutorData?.id} />
          </CardContent>
        </Card>
      </TabsContent>

      {/* About Tab */}
      <TabsContent value="about" className="mt-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center">
              <BookOpen className="h-4 w-4 mr-2 text-blue-600" />
              About Me
            </CardTitle>
            {isOwnProfile && onEditSection && (
              <Button
                onClick={() => onEditSection('about')}
                size="sm"
                variant="outline"
              >
                <Settings className="h-3 w-3 mr-1" />
                Edit
              </Button>
            )}
          </CardHeader>
          <CardContent>
            <div className="prose max-w-none">
              <p className={`text-gray-700 leading-relaxed ${!showFullBio ? 'line-clamp-3' : ''}`}>
                {tutorData?.bio || 'No bio provided yet.'}
              </p>
              {tutorData?.bio && tutorData.bio.length > 150 && (
                <Button
                  variant="link"
                  className="mt-2 p-0 h-auto text-blue-600"
                  onClick={() => setShowFullBio(!showFullBio)}
                >
                  {showFullBio ? 'See less' : 'See more'}
                </Button>
              )}
            </div>
            
            {tutorData?.shortBio && (
              <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h4 className="font-semibold text-blue-900 mb-2">Quick Summary</h4>
                <p className="text-blue-800">{tutorData.shortBio}</p>
              </div>
            )}
          </CardContent>
        </Card>
      </TabsContent>

      {/* Subjects Tab */}
      <TabsContent value="subjects" className="mt-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center">
              <BookOpen className="h-4 w-4 mr-2 text-purple-600" />
              My Subjects
            </CardTitle>
            {isOwnProfile && onEditSection && (
              <Button
                onClick={() => onEditSection('subjects')}
                size="sm"
                variant="outline"
              >
                <Settings className="h-3 w-3 mr-1" />
                Edit
              </Button>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            {defaultOnes.length > 0 && (
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Core Subjects</h4>
                <div className="flex flex-wrap gap-2">
                  {defaultOnes.map((subject, index) => (
                    <Badge key={index} variant="secondary" className="text-sm">
                      {subject}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            
            {approvedOnes.length > 0 && (
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Specialized Subjects</h4>
                <div className="flex flex-wrap gap-2">
                  {approvedOnes.map((subject, index) => (
                    <Badge key={index} className="text-sm bg-purple-100 text-purple-800 border-purple-200">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      {subject}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            
            {(!tutorData?.subjects || tutorData.subjects.length === 0) && (
              <p className="text-gray-500 text-center py-8">No subjects added yet</p>
            )}
          </CardContent>
        </Card>
      </TabsContent>

      {/* Experience & Education Tab */}
      <TabsContent value="experience" className="mt-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center">
              <GraduationCap className="h-4 w-4 mr-2 text-blue-600" />
              Experience & Education
            </CardTitle>
            {isOwnProfile && onEditSection && (
              <Button
                onClick={() => onEditSection('experience')}
                size="sm"
                variant="outline"
              >
                <Settings className="h-3 w-3 mr-1" />
                Edit
              </Button>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                <Award className="h-4 w-4 mr-2 text-orange-500" />
                Teaching Experience
              </h4>
              <p className="text-gray-600">{renderExperience(tutorData?.experience) || 'No experience details provided'}</p>
            </div>

            {tutorData?.education && Array.isArray(tutorData.education) && tutorData.education.length > 0 && (
              <div>
                <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                  <GraduationCap className="h-4 w-4 mr-2 text-blue-500" />
                  Education
                </h4>
                <div className="space-y-3">
                  {tutorData.education.map((edu: any, index: number) => (
                    <div key={index} className="border-l-4 border-blue-500 pl-4 py-2">
                      <div className="font-medium text-gray-900">{edu.degree}</div>
                      <div className="text-gray-600">{edu.institution}</div>
                      <div className="text-sm text-gray-500">Graduated: {edu.year}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div>
              <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                <Globe className="h-4 w-4 mr-2 text-green-500" />
                Languages
              </h4>
              <div className="flex flex-wrap gap-2">
                {tutorData?.languages && tutorData.languages.length > 0 ? (
                  tutorData.languages.map((language: string, index: number) => (
                    <Badge key={index} variant="outline">
                      {language}
                    </Badge>
                  ))
                ) : (
                  <span className="text-gray-500">No languages specified</span>
                )}
              </div>
            </div>

            {tutorData?.grades && tutorData.grades.length > 0 && (
              <div>
                <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                  <GraduationCap className="h-4 w-4 mr-2 text-purple-500" />
                  Grade Levels
                </h4>
                <div className="flex flex-wrap gap-2">
                  {tutorData.grades.map((grade: string, index: number) => (
                    <Badge key={index} variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                      {grade}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </TabsContent>

      {/* Availability Tab */}
      <TabsContent value="availability" className="mt-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Calendar className="h-4 w-4 mr-2 text-green-600" />
              My Availability
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {tutorData?.availability?.timeSlots && tutorData.availability.timeSlots.length > 0 ? (
              <>
                <div className="flex justify-center">
                  <CalendarComponent
                    mode="single"
                    selected={selectedAvailabilityDate}
                    onSelect={setSelectedAvailabilityDate}
                    className="rounded-md border pointer-events-auto"
                    disabled={(date) => {
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);

                      // Disable past dates
                      if (date < today) return true;

                      // Disable days without availability like tutor details page
                      if (!tutorData?.availability?.timeSlots) return true;
                      const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
                      const daySlot = tutorData.availability.timeSlots.find(
                        (slot: any) => slot.day === dayName
                      );

                      return !daySlot || !daySlot.slots || daySlot.slots.length === 0;
                    }}
                    modifiers={{
                      available: (date) => {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        if (date < today) return false;
                        if (!tutorData?.availability?.timeSlots) return false;

                        const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
                        const daySlot = tutorData.availability.timeSlots.find(
                          (slot: any) => slot.day === dayName
                        );

                        return !!daySlot && !!daySlot.slots && daySlot.slots.length > 0;
                      },
                      booked: getDaysWithBookings(),
                    }}
                    modifiersStyles={{
                      available: {
                        backgroundColor: 'rgba(59, 130, 246, 0.1)',
                        color: '#2563eb',
                        fontWeight: 'bold',
                      },
                      booked: {
                        backgroundColor: '#dbeafe',
                        color: '#1e40af',
                        fontWeight: 'bold',
                      },
                    }}
                  />
                </div>

                {isLoadingBookings && (
                  <div className="text-center text-sm text-muted-foreground">
                    Loading bookings...
                  </div>
                )}

                {selectedAvailabilityDate && (
                  <div className="space-y-4">
                    {/* Booked Time Slots */}
                    {bookedTimeSlots.length > 0 && (
                      <div>
                        <h4 className="font-semibold mb-2 flex items-center text-primary">
                          <CheckCircle className="h-4 w-4 mr-2" />
                          Booked Times for {selectedAvailabilityDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                        </h4>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                          {bookedTimeSlots.map((booking: any, index: number) => (
                            <Badge key={index} className="justify-center py-2 bg-primary/10 text-primary border-primary">
                              <Clock className="h-3 w-3 mr-1" />
                              {booking.startTime} - {booking.endTime}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Available Time Slots */}
                    {availableTimeSlots.length > 0 && (
                      <div>
                        <h4 className="font-semibold mb-2 flex items-center text-green-600">
                          <Clock className="h-4 w-4 mr-2" />
                          Available Times for {selectedAvailabilityDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                        </h4>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                          {availableTimeSlots.map((slot: any, index: number) => (
                            <Badge key={index} variant="outline" className="justify-center py-2">
                              <Clock className="h-3 w-3 mr-1" />
                              {slot.start} - {slot.end}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {!bookedTimeSlots.length && !availableTimeSlots.length && (
                      <div className="text-center py-4 text-muted-foreground">
                        No availability set for this date
                      </div>
                    )}
                  </div>
                )}

                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Weekly Schedule</h4>
                  <div className="space-y-2">
                    {tutorData.availability.timeSlots.map((daySlot: any, index: number) => (
                      <div key={index} className="flex justify-between items-start">
                        <span className="font-medium text-gray-700">{daySlot.day}:</span>
                        <div className="flex flex-wrap gap-1 justify-end">
                          {daySlot.slots && daySlot.slots.length > 0 ? (
                            daySlot.slots.map((slot: any, slotIndex: number) => (
                              <Badge key={slotIndex} variant="secondary" className="text-xs">
                                {slot.start}-{slot.end}
                              </Badge>
                            ))
                          ) : (
                            <span className="text-gray-500 text-sm">Not available</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            ) : (
              <p className="text-gray-500 text-center py-8">No availability schedule set</p>
            )}
          </CardContent>
        </Card>
      </TabsContent>

      {/* Pricing Tab */}
      <TabsContent value="pricing" className="mt-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center">
              <DollarSign className="h-4 w-4 mr-2 text-green-600" />
              Pricing Information
            </CardTitle>
            {isOwnProfile && onEditSection && (
              <Button
                onClick={() => onEditSection('pricing')}
                size="sm"
                variant="outline"
              >
                <Settings className="h-3 w-3 mr-1" />
                Edit
              </Button>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            {tutorData?.pricingType === 'hourly' && tutorData.hourlyRate > 0 && (
              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                <h4 className="font-semibold text-green-900 mb-2">Hourly Rate</h4>
                <p className="text-2xl font-bold text-green-700">${tutorData.hourlyRate}/hour</p>
              </div>
            )}

            {tutorData?.pricingType === 'session' && tutorData.sessionRate > 0 && (
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h4 className="font-semibold text-blue-900 mb-2">Per Session Rate</h4>
                <p className="text-2xl font-bold text-blue-700">${tutorData.sessionRate}/session</p>
              </div>
            )}

            {tutorData?.pricingType === 'level' && tutorData.levelRates && (
              <div className="space-y-2">
                <h4 className="font-semibold text-gray-900 mb-2">Rates by Level</h4>
                {tutorData.levelRates.beginner > 0 && (
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
                    <span className="font-medium">Beginner</span>
                    <span className="font-bold text-green-700">${tutorData.levelRates.beginner}/hour</span>
                  </div>
                )}
                {tutorData.levelRates.intermediate > 0 && (
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
                    <span className="font-medium">Intermediate</span>
                    <span className="font-bold text-blue-700">${tutorData.levelRates.intermediate}/hour</span>
                  </div>
                )}
                {tutorData.levelRates.advanced > 0 && (
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
                    <span className="font-medium">Advanced</span>
                    <span className="font-bold text-purple-700">${tutorData.levelRates.advanced}/hour</span>
                  </div>
                )}
              </div>
            )}

            {tutorData?.pricingType === 'monthly' && tutorData.monthlyRate > 0 && (
              <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                <h4 className="font-semibold text-purple-900 mb-2">Monthly Subscription</h4>
                <p className="text-2xl font-bold text-purple-700">${tutorData.monthlyRate}/month</p>
              </div>
            )}

            {(!tutorData?.pricingType || (
              tutorData.hourlyRate === 0 && 
              tutorData.sessionRate === 0 && 
              tutorData.monthlyRate === 0
            )) && (
              <p className="text-gray-500 text-center py-8">No pricing information set</p>
            )}

            {tutorData?.paymentMethods && tutorData.paymentMethods.length > 0 && (
              <div className="mt-4 pt-4 border-t">
                <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                  <CreditCard className="h-4 w-4 mr-2 text-green-600" />
                  Accepted Payment Methods
                </h4>
                <div className="flex flex-wrap gap-2">
                  {tutorData.paymentMethods.map((method: string, index: number) => {
                    const Icon = method.toLowerCase().includes('insta') ? Smartphone : CreditCard;
                    return (
                      <Badge key={index} variant="secondary" className="bg-green-100 text-green-700">
                        <Icon className="h-3 w-3 mr-1" />
                        {method}
                      </Badge>
                    );
                  })}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </TabsContent>

      {/* Lessons Tab */}
      <TabsContent value="lessons" className="mt-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center">
              <BookOpen className="h-4 w-4 mr-2 text-blue-600" />
              My Lessons
            </CardTitle>
            {isOwnProfile && (
              <Button
                onClick={() => setIsCreateLessonModalOpen(true)}
                size="sm"
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                <Plus className="h-3 w-3 mr-1" />
                Create Lesson
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {lessons && lessons.length > 0 ? (
              <div className="space-y-4">
                {lessons.slice(0, 6).map((lesson: any) => (
                  <div key={lesson.id} className="p-4 border rounded-lg hover:border-primary transition-colors">
                    <div className="flex justify-between items-start mb-2">
                      <h4 className="font-semibold text-lg">{lesson.title}</h4>
                      <Badge>{lesson.subject}</Badge>
                    </div>
                    {lesson.description && (
                      <p className="text-gray-600 text-sm mb-2 line-clamp-2">{lesson.description}</p>
                    )}
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      {lesson.duration && (
                        <span className="flex items-center">
                          <Clock className="h-3 w-3 mr-1" />
                          {lesson.duration} min
                        </span>
                      )}
                      {lesson.lessonType && (
                        <Badge variant="outline">{lesson.lessonType}</Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">No lessons created yet</p>
            )}
          </CardContent>
        </Card>
      </TabsContent>

      {/* Video Tab */}
      <TabsContent value="video" className="mt-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center">
              <Video className="h-4 w-4 mr-2 text-red-600" />
              Introduction Video
            </CardTitle>
            {isOwnProfile && onEditSection && (
              <Button
                onClick={() => onEditSection('video')}
                size="sm"
                variant="outline"
              >
                <Settings className="h-3 w-3 mr-1" />
                Edit
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {(() => {
              // Check for uploaded video file first
              const hasUploadedVideo = tutorData?.introVideoFile && tutorData.introVideoFile.trim();
              const hasVideoUrl = tutorData?.introVideo && tutorData.introVideo.trim();
              const hasLegacyVideo = tutorData?.youtubeVideoUrl && tutorData.youtubeVideoUrl.trim();
              
              if (hasUploadedVideo) {
                // Display uploaded video file
                const videoUrl = pb.files.getURL(tutorData, tutorData.introVideoFile);
                
                return (
                  <div className="space-y-2">
                    <div className="aspect-video rounded-lg overflow-hidden bg-gray-900">
                      <video
                        src={videoUrl}
                        className="w-full h-full object-contain"
                        controls
                        preload="metadata"
                        playsInline
                      >
                        Your browser does not support the video tag.
                      </video>
                    </div>
                    <p className="text-xs text-gray-500 text-center">Uploaded introduction video</p>
                  </div>
                );
              } else if (hasVideoUrl) {
                // Check if it's a Google Drive URL
                const isGoogleDrive = tutorData.introVideo.includes('drive.google.com');
                const embedUrl = convertToEmbeddableUrl(tutorData.introVideo);
                
                if (isGoogleDrive) {
                  // Google Drive: Show warning and direct link
                  return (
                    <div className="space-y-3">
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
                        <p className="text-amber-800 font-medium mb-1">⚠️ Google Drive videos cannot be embedded</p>
                        <p className="text-amber-700">Click the button below to watch the introduction video directly.</p>
                      </div>
                      
                      <Button
                        variant="default"
                        size="lg"
                        className="w-full bg-blue-600 hover:bg-blue-700"
                        onClick={() => window.open(embedUrl, '_blank')}
                      >
                        <Video className="h-5 w-5 mr-2" />
                        Watch Introduction Video
                      </Button>
                    </div>
                  );
                } else {
                  // YouTube, Vimeo, or other embeddable videos: Show iframe
                  return (
                    <div className="aspect-video w-full rounded-lg overflow-hidden bg-gray-900">
                      <iframe
                        className="w-full h-full"
                        src={embedUrl}
                        title="Introduction Video"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                  );
                }
              } else if (hasLegacyVideo) {
                // Fallback to legacy youtubeVideoUrl field
                return (
                  <div className="aspect-video w-full rounded-lg overflow-hidden bg-gray-900">
                    <iframe
                      className="w-full h-full"
                      src={convertToEmbeddableUrl(tutorData.youtubeVideoUrl)}
                      title="Introduction Video"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                );
              } else {
                // No video available
                return (
                  <div className="text-center py-12 text-gray-500">
                    <Video className="h-12 w-12 mx-auto mb-3 text-gray-400" />
                    <p>No introduction video added yet</p>
                    {isOwnProfile && (
                      <Button
                        onClick={() => onEditSection?.('video')}
                        size="sm"
                        variant="outline"
                        className="mt-4"
                      >
                        Add Video
                      </Button>
                    )}
                  </div>
                );
              }
            })()}
          </CardContent>
        </Card>
      </TabsContent>

      {/* Reviews Tab */}
      <TabsContent value="reviews" className="mt-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Star className="h-4 w-4 mr-2 text-yellow-500" />
              Reviews
            </CardTitle>
          </CardHeader>
          <CardContent>
            <TutorReviews tutorId={tutorData?.id} canAddReview={false} />
          </CardContent>
        </Card>
      </TabsContent>

      {/* Settings Tab (Only for own profile) */}
      {isOwnProfile && (
        <TabsContent value="settings" className="mt-4 space-y-4">
          {/* Profile Picture Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center">
                  <Upload className="h-4 w-4 mr-2" />
                  Profile Picture
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowProfilePictureSection(!showProfilePictureSection)}
                >
                  {showProfilePictureSection ? 'Cancel' : 'Change'}
                </Button>
              </CardTitle>
            </CardHeader>
            {showProfilePictureSection && (
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="picture-upload" className="text-sm font-medium">
                    Upload from Device
                  </Label>
                  <Input
                    id="picture-upload"
                    type="file"
                    accept="image/*"
                    onChange={handleFileUpload}
                    disabled={isUploadingPicture}
                    className="mt-1"
                  />
                </div>
                
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-white px-2 text-muted-foreground">Or</span>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="picture-url" className="text-sm font-medium flex items-center">
                    <LinkIcon className="h-3 w-3 mr-1" />
                    Image URL
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      id="picture-url"
                      type="url"
                      placeholder="https://example.com/image.jpg"
                      value={profilePictureUrlInput}
                      onChange={(e) => setProfilePictureUrl(e.target.value)}
                      disabled={isUploadingPicture}
                    />
                    <Button
                      onClick={handleUrlSubmit}
                      disabled={!profilePictureUrlInput.trim() || isUploadingPicture}
                    >
                      Set
                    </Button>
                  </div>
                </div>
              </CardContent>
            )}
          </Card>

          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button 
                onClick={() => setIsEditModalOpen(true)}
                className="w-full justify-start"
                variant="outline"
              >
                <Settings className="h-4 w-4 mr-2" />
                Edit Profile
              </Button>
              
              <Button
                onClick={() => setIsChangePasswordModalOpen(true)}
                className="w-full justify-start"
                variant="outline"
              >
                <Settings className="h-4 w-4 mr-2" />
                Password
              </Button>

              <Button
                onClick={() => setIsAddStoryModalOpen(true)}
                className="w-full justify-start"
                variant="outline"
              >
                <MapPin className="h-4 w-4 mr-2" />
                Add Travel Story
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      )}
    </Tabs>

    <CreateLessonModal
      isOpen={isCreateLessonModalOpen}
      onClose={() => setIsCreateLessonModalOpen(false)}
      onLessonCreated={() => {
        setIsCreateLessonModalOpen(false);
        // Optionally refresh lessons data if needed
      }}
    />
    </>
  );
};

export default TutorProfileTabs;
